/**
 * @fileoverview CanvasPanel — TipTap-backed AI-authored report surface
 *
 * Mounted next to the chat in the split-view Agent surface. The user
 * edits freely; the agent stages updates via `proposeCanvasPatch`
 * which surface as Apply/Reject cards in the chat panel (handled by
 * `StructuredDiffCard`, which delegates `canvas_patch` kinds to the
 * canvas patch handler in `applyAgentProposal`).
 *
 * Why this is intentionally simple:
 * - The precedent template editor is 2000+ lines because it handles
 *   placeholder fields, branding, signatures, page rules, etc.
 *   Canvas is a free-form scratchpad — it doesn't need any of that.
 * - We re-use the existing chat diff card for AI updates so there's
 *   no separate canvas-specific Apply/Reject UX.
 *
 * Autosave on blur (matches the established `useAutosave` pattern
 * across the app).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TableKit } from "@tiptap/extension-table";
import { ChartNode } from "./canvas/ChartNode";
import { DiagramNode } from "./canvas/DiagramNode";
import { SpreadsheetNode } from "./canvas/SpreadsheetNode";
import { SignatureNode } from "./canvas/SignatureNode";
import {
  buildSignatureSpecForUser,
  htmlHasSignaturePlaceholder,
  resolveSignaturePlaceholders,
  specToSignatureHtml,
} from "./canvas/signatureHtml";
import { toast } from "sonner";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { cn } from "@/utils/misc";
import { Button } from "@/ui/primitives/button";
import {
  Bold,
  Italic,
  Heading1,
  Heading2,
  List,
  ListOrdered,
  Undo2,
  Redo2,
  Download,
  FileText,
  Loader2,
  PenLine,
  Sparkles,
  Save,
  CheckCircle2,
  FilePlus2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/ui/composed/menus/dropdown-menu";
import {
  buildCanvasExportBlob,
  canvasExportFileName,
  CANVAS_EXPORT_LABEL,
  CANVAS_EXPORT_MIME,
  type CanvasExportDestination,
  type CanvasExportFormat,
} from "@/utils/canvas/canvasExport";
import { AiDisclaimer } from "@/ui/composed/ai-disclaimer";

interface CanvasPanelProps {
  /** The job this canvas can be saved to as a PDF. */
  jobId: Id<"jobs">;
  /**
   * The chat thread that owns this canvas. Each thread has its own canvas
   * (Claude-artifact lifecycle). Null when no thread is active yet — the
   * canvas then shows blank and edits are not persisted until a thread exists.
   */
  threadId: string | null;
  className?: string;
}

const EMPTY_HTML = "<p></p>";

export function CanvasPanel({ jobId, threadId, className }: CanvasPanelProps) {
  const { data: canvas, isLoading } = useQuery({
    ...convexQuery(api.agents.universal.canvas.getMyThreadCanvas, {
      threadId: threadId ?? undefined,
    }),
    enabled: !!threadId,
  });
  const { data: currentUser } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );

  const { mutateAsync: upsertCanvas, isPending: isSaving } = useMutation({
    mutationFn: useConvexMutation(
      api.agents.universal.canvas.upsertThreadCanvas,
    ),
  });
  const { mutateAsync: clearCanvas } = useMutation({
    mutationFn: useConvexMutation(
      api.agents.universal.canvas.clearThreadCanvas,
    ),
  });
  const generateUploadUrl = useConvexMutation(
    api.jobs.gatherInformation.documents.mutations.generateUploadUrl,
  );
  const saveCanvasAsDocument = useConvexMutation(
    api.agents.universal.canvas.saveCanvasAsDocument,
  );

  const [title, setTitle] = useState("");
  const [isSavingToJob, setIsSavingToJob] = useState(false);
  const [lastSavedDocumentId, setLastSavedDocumentId] =
    useState<Id<"documents"> | null>(null);
  const lastSavedHtmlRef = useRef<string>("");

  const editor = useEditor({
    extensions: [
      StarterKit,
      TableKit.configure({ table: { resizable: true } }),
      ChartNode,
      DiagramNode,
      SpreadsheetNode,
      SignatureNode,
    ],
    content: EMPTY_HTML,
    editorProps: {
      attributes: {
        class:
          "prose prose-sm dark:prose-invert max-w-none focus:outline-none min-h-[400px] p-4",
      },
    },
  });

  // Hydrate editor + title when the canvas loads or changes server-side
  // (e.g. an agent's `Apply` mutation just replaced the HTML), and force a
  // full re-hydrate when the active thread changes (switching threads must
  // swap the canvas wholesale — that's the per-thread lifecycle).
  const prevThreadRef = useRef<string | null>(null);
  useEffect(() => {
    if (!editor) return;
    const incoming = canvas?.html ?? EMPTY_HTML;
    const threadChanged = prevThreadRef.current !== threadId;
    if (!threadChanged) {
      if (incoming === lastSavedHtmlRef.current) return;
      if (editor.getHTML() === incoming) return;
    }
    editor.commands.setContent(incoming, { emitUpdate: false });
    lastSavedHtmlRef.current = incoming;
    setTitle(canvas?.title ?? "");
    prevThreadRef.current = threadId;
  }, [canvas?.html, canvas?.title, editor, threadId]);

  // The agent can't see the user's signature, so it stages placeholder
  // signature blocks (`data-morgan-signature='{}'`). Whenever the canvas
  // contains an unresolved placeholder and the user's saved signature is
  // known, bake the signature into the block (data URI, so it survives
  // export and doesn't depend on an expiring storage URL). Persisted by the
  // next autosave/export flush like any other edit.
  useEffect(() => {
    if (!editor || !currentUser) return;
    const html = editor.getHTML();
    if (!htmlHasSignaturePlaceholder(html)) return;
    let cancelled = false;
    void (async () => {
      const spec = await buildSignatureSpecForUser(currentUser);
      if (!spec || cancelled) return;
      // Skip if the user kept typing while the signature image loaded.
      if (editor.getHTML() !== html) return;
      const { html: resolved, changed } = resolveSignaturePlaceholders(
        html,
        spec,
      );
      if (changed) {
        editor.commands.setContent(resolved, { emitUpdate: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editor, currentUser, canvas?.html]);

  /**
   * Insert the current user's saved e-signature at the cursor. Points the
   * user to Settings when no signature is configured yet.
   */
  const [isInsertingSignature, setIsInsertingSignature] = useState(false);
  const handleInsertSignature = useCallback(async () => {
    if (!editor || !currentUser) return;
    setIsInsertingSignature(true);
    try {
      const spec = await buildSignatureSpecForUser(currentUser);
      if (!spec) {
        toast.info("No signature saved yet", {
          description:
            "Add your signature under Settings → Signature, then try again.",
        });
        return;
      }
      editor.chain().focus().insertContent(specToSignatureHtml(spec)).run();
    } catch (err) {
      console.error("[CanvasPanel] insert signature failed", err);
      toast.error("Could not insert your signature");
    } finally {
      setIsInsertingSignature(false);
    }
  }, [editor, currentUser]);

  // Autosave on blur — matches the rest of the app's `useAutosave` flow.
  const handleBlur = useCallback(async () => {
    if (!editor || !threadId) return;
    const html = editor.getHTML();
    if (html === lastSavedHtmlRef.current) return;
    try {
      await upsertCanvas({
        threadId,
        title: title || "Untitled report",
        html,
      });
      lastSavedHtmlRef.current = html;
    } catch (err) {
      console.error("[CanvasPanel] autosave failed", err);
    }
  }, [editor, threadId, title, upsertCanvas]);

  const handleTitleBlur = useCallback(async () => {
    if (!editor || !threadId) return;
    const html = editor.getHTML();
    try {
      await upsertCanvas({
        threadId,
        title: title || "Untitled report",
        html,
      });
      lastSavedHtmlRef.current = html;
    } catch (err) {
      console.error("[CanvasPanel] title autosave failed", err);
    }
  }, [editor, threadId, title, upsertCanvas]);

  /**
   * Export the canvas as a PDF, Word or Excel file — either downloaded
   * directly or uploaded to Convex storage and attached as a
   * `firm_generated` document on the job. Saved files skip the
   * Reducto/Anthropic processing pipeline because the content originated
   * here.
   */
  const handleExport = useCallback(
    async (
      format: CanvasExportFormat,
      destination: CanvasExportDestination,
    ) => {
      if (!editor) return;
      setIsSavingToJob(true);
      try {
        // Flush any unsaved canvas edits first so the export reflects the
        // exact HTML stored server-side.
        const html = editor.getHTML();
        if (threadId && html !== lastSavedHtmlRef.current) {
          await upsertCanvas({
            threadId,
            title: title || "Untitled report",
            html,
          });
          lastSavedHtmlRef.current = html;
        }

        const reportTitle = title.trim() || "Morgan Canvas Report";
        const preparerName = currentUser
          ? [currentUser.firstName, currentUser.lastName]
              .filter(Boolean)
              .join(" ") || currentUser.email
          : null;
        const footer = [
          preparerName ? `Prepared by ${preparerName}` : null,
          `Generated by Morgan · ${new Date().toLocaleString("en-AU")}`,
        ]
          .filter(Boolean)
          .join(" · ");

        const blob = await buildCanvasExportBlob({
          html,
          title: reportTitle,
          footer,
          format,
        });
        const fileName = canvasExportFileName(reportTitle, format);

        if (destination === "download") {
          const { downloadBlob } =
            await import("@/utils/export/exportHtmlDocument");
          downloadBlob(blob, fileName);
          toast.success(`${CANVAS_EXPORT_LABEL[format]} downloaded`);
          return;
        }

        const uploadUrl = await generateUploadUrl({ jobId });
        const uploadResponse = await fetch(uploadUrl, {
          method: "POST",
          headers: { "Content-Type": CANVAS_EXPORT_MIME[format] },
          body: blob,
        });
        if (!uploadResponse.ok) {
          throw new Error("Upload to storage failed");
        }
        const { storageId } = (await uploadResponse.json()) as {
          storageId: Id<"_storage">;
        };

        const documentId = await saveCanvasAsDocument({
          jobId,
          storageId,
          fileName,
          fileSize: blob.size,
          canvasTitle: reportTitle,
          fileType: CANVAS_EXPORT_MIME[format],
        });
        setLastSavedDocumentId(documentId);
        toast.success("Saved to job", {
          description: `${fileName} is now in the job's documents.`,
        });
      } catch (err) {
        console.error("[CanvasPanel] export failed", err);
        toast.error(
          destination === "download"
            ? "Could not export the canvas"
            : "Could not save to job",
          {
            description:
              err instanceof Error
                ? err.message
                : "Unexpected error while exporting the canvas.",
          },
        );
      } finally {
        setIsSavingToJob(false);
      }
    },
    [
      editor,
      jobId,
      threadId,
      title,
      currentUser,
      upsertCanvas,
      generateUploadUrl,
      saveCanvasAsDocument,
    ],
  );

  const handleClearCanvas = useCallback(async () => {
    if (!editor || !threadId) return;
    try {
      await clearCanvas({ threadId });
      editor.commands.setContent(EMPTY_HTML, { emitUpdate: false });
      lastSavedHtmlRef.current = "";
      setTitle("");
      setLastSavedDocumentId(null);
      toast.success("Canvas cleared", {
        description: "You can ask Morgan to generate something new.",
      });
    } catch (err) {
      console.error("[CanvasPanel] clear failed", err);
      toast.error("Could not clear canvas");
    }
  }, [editor, threadId, clearCanvas]);

  if (isLoading || !editor) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-xl border border-border bg-card",
          className,
        )}
      >
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm",
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-border bg-background/80 px-4 py-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary shadow-sm">
          <FileText className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={handleTitleBlur}
            placeholder="Untitled report"
            className="w-full bg-transparent text-sm font-semibold outline-none placeholder:text-muted-foreground/60"
          />
          <p className="text-xs text-muted-foreground">
            Use the chat to ask Morgan to make updates; redraft sections; add
            tables, etc.
          </p>
        </div>
        {isSaving && (
          <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            Saving…
          </span>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearCanvas}
          disabled={!canvas?.html}
          className="shrink-0 gap-1.5 text-muted-foreground hover:text-foreground"
          title="Clear the canvas and start fresh"
        >
          <FilePlus2 className="h-3.5 w-3.5" />
          New
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              disabled={isSavingToJob || !canvas?.html}
              className="shrink-0 gap-1.5"
              title="Save the canvas to this job's documents or download it as a PDF, Word or Excel file"
            >
              {isSavingToJob ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : lastSavedDocumentId ? (
                <CheckCircle2 className="h-3.5 w-3.5" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}
              {isSavingToJob
                ? "Saving…"
                : lastSavedDocumentId
                  ? "Saved to job"
                  : "Save / Export"}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel>Save to job documents</DropdownMenuLabel>
            {(["pdf", "docx", "xlsx"] as const).map((format) => (
              <DropdownMenuItem
                key={`save-${format}`}
                onClick={() => void handleExport(format, "documents")}
              >
                <FileText className="mr-2 h-3.5 w-3.5" />
                Save as {CANVAS_EXPORT_LABEL[format]}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Download</DropdownMenuLabel>
            {(["pdf", "docx", "xlsx"] as const).map((format) => (
              <DropdownMenuItem
                key={`download-${format}`}
                onClick={() => void handleExport(format, "download")}
              >
                <Download className="mr-2 h-3.5 w-3.5" />
                Download as {CANVAS_EXPORT_LABEL[format]}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border bg-background/50 px-2 py-1.5">
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-7 w-7 p-0",
            editor.isActive("bold") && "bg-accent text-accent-foreground",
          )}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-7 w-7 p-0",
            editor.isActive("italic") && "bg-accent text-accent-foreground",
          )}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="h-3.5 w-3.5" />
        </Button>
        <div className="mx-1 h-4 w-px bg-border" />
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-7 w-7 p-0",
            editor.isActive("heading", { level: 1 }) &&
              "bg-accent text-accent-foreground",
          )}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
        >
          <Heading1 className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-7 w-7 p-0",
            editor.isActive("heading", { level: 2 }) &&
              "bg-accent text-accent-foreground",
          )}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
        >
          <Heading2 className="h-3.5 w-3.5" />
        </Button>
        <div className="mx-1 h-4 w-px bg-border" />
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-7 w-7 p-0",
            editor.isActive("bulletList") && "bg-accent text-accent-foreground",
          )}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-7 w-7 p-0",
            editor.isActive("orderedList") &&
              "bg-accent text-accent-foreground",
          )}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="h-3.5 w-3.5" />
        </Button>
        <div className="mx-1 h-4 w-px bg-border" />
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-1.5"
          onClick={() => void handleInsertSignature()}
          disabled={isInsertingSignature || !currentUser}
          title="Insert your saved e-signature at the cursor"
        >
          {isInsertingSignature ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <PenLine className="h-3.5 w-3.5" />
          )}
          <span className="text-xs">Sign</span>
        </Button>
        <div className="mx-1 h-4 w-px bg-border" />
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
        >
          <Undo2 className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
        >
          <Redo2 className="h-3.5 w-3.5" />
        </Button>
        <div className="ml-auto pr-1">
          <span className="inline-flex items-center gap-1 rounded-md border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300">
            <Sparkles className="h-3 w-3" />
            AI-aware
          </span>
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 overflow-y-auto bg-background" onBlur={handleBlur}>
        <EditorContent editor={editor} />
        <AiDisclaimer className="px-8 pb-6 pt-4" />
      </div>
    </div>
  );
}
